import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Button, Space, message, Badge } from 'antd';
import { ReloadOutlined, GlobalOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as nationwideDashboardService from '../../services/dashboard/nationwideDashboardService';
import ExecutiveSummary from './components/ExecutiveSummary';
import RealtimeOperations from './components/RealtimeOperations';
import RankingsPanel from './components/RankingsPanel';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

/**
 * Nationwide Dashboard - 全国看板
 * Route: /nationwide-dashboard
 */
const DEFAULT_STORE_TYPE = 'Go Fresh';

const NationwideDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [storeType, setStoreType] = useState(DEFAULT_STORE_TYPE);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await nationwideDashboardService.getNationwideDashboardData(storeType);
      if (response.success) {
        setData(response.data);
        setLastRefreshTime(dayjs().format('HH:mm:ss'));
      } else {
        message.error(response.errorMessage || t('dashboard.nationwide.loadFailed'));
      }
    } catch (error) {
      console.error('Nationwide Dashboard fetch error:', error);
      message.error(t('dashboard.nationwide.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, storeType]);

  return (
    <Spin spinning={loading && !data}>
      <div style={{ padding: '0 0 16px 0', background: '#F0F2F5', minHeight: '100vh', overflow: 'hidden' }}>
        {/* Global Header Bar - sticky top */}
        <div
          style={{
            background: '#fff',
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {/* Left: Title */}
            <Space size={12}>
              <GlobalOutlined style={{ fontSize: 20, color: '#00A651' }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>{t('dashboard.nationwide.title')}</span>
              <Badge
                status={autoRefresh ? 'processing' : 'default'}
                text={<span style={{ fontSize: 12, color: '#999' }}>{autoRefresh ? t('dashboard.live') : t('dashboard.paused')}</span>}
              />
            </Space>

            {/* Right: Refresh controls */}
            <Space size={8}>
              {lastRefreshTime && (
                <span style={{ color: '#999', fontSize: 12 }}>
                  {t('dashboard.updated')}: {lastRefreshTime}
                </span>
              )}
              <Button icon={<ReloadOutlined />} size="small" onClick={fetchData} loading={loading}>
                {t('dashboard.refresh')}
              </Button>
            </Space>
          </div>
        </div>

        {/* Module 1: Executive Summary */}
        <div style={{ marginBottom: 16 }}>
          <ExecutiveSummary data={data?.executiveSummary} realtimeData={data?.realtimeOperations} />
        </div>

        {/* Module 2: Real-time Operations & Alerts */}
        <div style={{ marginBottom: 16 }}>
          <RealtimeOperations data={data?.realtimeOperations} summaryData={data?.executiveSummary} />
        </div>

        {/* Module 3: Rankings & Actionables */}
        <div style={{ marginBottom: 16 }}>
          <RankingsPanel data={data?.rankings} storeType={storeType} onStoreTypeChange={setStoreType} />
        </div>
      </div>
    </Spin>
  );
};

export default NationwideDashboard;
